import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  insertSlides,
  arrayBufferToBase64,
} from '../../services/powerpointSlideService';
import { extractSlideIds } from '../../services/pptxService';
import { presentationMessages } from '../../intl/messages';
import type { SlideInfo } from './types';

interface UseInsertSlidesResult {
  insert: (
    selectedIndices: Set<number>,
    slides: SlideInfo[],
    downloadUrl: string
  ) => Promise<void>;
  inserting: boolean;
  success: boolean;
  error: string | null;
  clearStatus: () => void;
}

export function useInsertSlides(): UseInsertSlidesResult {
  const intl = useIntl();

  const [inserting, setInserting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const insert = React.useCallback(
    async (
      selectedIndices: Set<number>,
      slides: SlideInfo[],
      downloadUrl: string
    ) => {
      setInserting(true);
      setError(null);
      setSuccess(false);

      try {
        const response = await fetch(downloadUrl);
        if (!response.ok) {
          throw new Error(
            intl.formatMessage(presentationMessages.errorDownload)
          );
        }

        const buffer = await response.arrayBuffer();
        const base64 = arrayBufferToBase64(buffer);

        // If the user selected a subset of slides, extract the PowerPoint slide
        // IDs from the PPTX XML and pass them to insertSlidesFromBase64.
        // A full selection (or no selection) inserts all slides without filtering.
        let sourceSlideIds: string[] | undefined;
        if (selectedIndices.size > 0 && selectedIndices.size < slides.length) {
          const allSlideIds = await extractSlideIds(buffer);
          sourceSlideIds = slides
            .filter(s => selectedIndices.has(s.index))
            .map(s => allSlideIds[s.index - 1])
            .filter(Boolean);
        }

        await insertSlides(base64, sourceSlideIds);
        setSuccess(true);
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : intl.formatMessage(presentationMessages.errorImport)
        );
      } finally {
        setInserting(false);
      }
    },
    [intl]
  );

  const clearStatus = React.useCallback(() => {
    setSuccess(false);
    setError(null);
  }, []);

  return { insert, inserting, success, error, clearStatus };
}
